import React, { useEffect, useState } from 'react';
import { BookOpen, FilePlus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';
import { useBoundStore } from '../../store';
import { useI18n } from '../../i18n';

export interface GenerateContractValues {
  docType: string;
  description: string;
  playbookId: string | null;
}

interface GenerateContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: GenerateContractValues) => void | Promise<void>;
  isSubmitting?: boolean;
  initialDocType?: string;
  initialDescription?: string;
  initialPlaybookId?: string | null;
}

const NO_PLAYBOOK = 'none';

export const GenerateContractDialog: React.FC<GenerateContractDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  initialDocType = '',
  initialDescription = '',
  initialPlaybookId = null,
}) => {
  const { t } = useI18n();
  const docTypeSuggestions = [
    t.qagentHome.generateDialog.docTypeSuggestions.nda,
    t.qagentHome.generateDialog.docTypeSuggestions.employmentAgreement,
    t.qagentHome.generateDialog.docTypeSuggestions.serviceContract,
    t.qagentHome.generateDialog.docTypeSuggestions.consultingAgreement,
  ];
  const [docType, setDocType] = useState(initialDocType);
  const [description, setDescription] = useState(initialDescription);
  const [playbookId, setPlaybookId] = useState<string | null>(initialPlaybookId);
  const playbooks = useBoundStore((state) => state.playbooks);
  const fetchPlaybooks = useBoundStore((state) => state.fetchPlaybooks);

  useEffect(() => {
    if (open) {
      setDocType(initialDocType);
      setDescription(initialDescription);
      setPlaybookId(initialPlaybookId);
      fetchPlaybooks();
    }
  }, [open, initialDocType, initialDescription, initialPlaybookId, fetchPlaybooks]);

  const selectedPlaybook = playbooks.find((p) => p.id === playbookId);

  const canSubmit = docType.trim().length > 0 && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      docType: docType.trim(),
      description: description.trim(),
      playbookId: selectedPlaybook ? selectedPlaybook.id : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-light text-brand">
              <FilePlus className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle>{t.qagentHome.generateDialog.title}</DialogTitle>
              <DialogDescription className="mt-0.5">
                {t.qagentHome.generateDialog.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-w-0 space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="generate-doc-type" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.qagentHome.generateDialog.docTypeLabel}
            </Label>
            <input
              id="generate-doc-type"
              type="text"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-brand"
              placeholder={t.qagentHome.generateDialog.docTypePlaceholder}
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {docTypeSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setDocType(s)}
                  className="px-2 py-0.5 rounded-full border border-border/60 bg-background hover:bg-brand-light/40 hover:border-brand/40 text-[11px] text-muted-foreground hover:text-brand transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="generate-description" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.qagentHome.generateDialog.descriptionLabel} <span className="normal-case font-normal text-muted-foreground/70">{t.qagentHome.generateDialog.descriptionOptional}</span>
            </Label>
            <textarea
              id="generate-description"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background resize-none focus:outline-none focus:ring-1 focus:ring-brand"
              placeholder={t.qagentHome.generateDialog.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="generate-playbook" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.qagentHome.generateDialog.playbookLabel} <span className="normal-case font-normal text-muted-foreground/70">{t.qagentHome.generateDialog.descriptionOptional}</span>
            </Label>
            <Select
              value={selectedPlaybook ? selectedPlaybook.id : NO_PLAYBOOK}
              onValueChange={(v) => setPlaybookId(v === NO_PLAYBOOK ? null : v)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="generate-playbook" className="w-full">
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen className="h-4 w-4 text-brand shrink-0" />
                  {selectedPlaybook ? (
                    <span className="text-sm truncate">{selectedPlaybook.name}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t.qagentHome.generateDialog.noPlaybook}</span>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PLAYBOOK}>
                  <span className="text-muted-foreground">{t.qagentHome.generateDialog.noPlaybook}</span>
                </SelectItem>
                {playbooks.map((playbook) => (
                  <SelectItem key={playbook.id} value={playbook.id}>
                    <div>
                      <div className="text-sm">{playbook.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t.qagentHome.generateDialog.playbookMeta(
                          playbook.agreementType,
                          playbook.rules.filter((r) => r.isActive).length,
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t.qagentHome.generateDialog.starting}
              </>
            ) : (
              t.qagentHome.generateDialog.submit
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateContractDialog;
